import { useMemo, useState } from 'react';
import {
    useGetMemberMutation,
    useAddMemberContributionMutation,
    useArchiveMemberContributionMutation,
    useGetMemberArchivedContributionsMutation,
    useGetMemberContributionsMutation,
    useUpdateMemberContributionMutation
} from '@services/member/member/member.endpoints';
import { Member, Contribution } from '@services/member/member/member.types';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { PaginationMetadata } from '@services/providers/cosem.types';

export const useDetailsContributionsVM = () => {
    const { id } = useParams();
    const [member, setMember] = useState<Member>();
    const contributionColumnHelper = createColumnHelper<Contribution>();
    const [needReload, setNeedReload] = useState<boolean>();
    const [totalContributions, setTotalContributions] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const MySwal = withReactContent(Swal);

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isUninitialized: isGetMemberUninitialized,
            isError: isGetMemberError,
            isSuccess: isGetMemberSuccess,
            error: getMemberError,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();

    const [
        updateMemberContribution,
        {
            isSuccess: isUpdateMemberContributionSuccess,
        }
    ] = useUpdateMemberContributionMutation();

    const [
        addMemberContribution,
        {
            isSuccess: isAddMemberContributionSuccess
        }
    ] = useAddMemberContributionMutation();

    const [
        archiveMemberContribution,
        {
            isSuccess: isArchiveMemberContributionSuccess
        }
    ] = useArchiveMemberContributionMutation();

    useEffect(() => {
        if (!id) return;
        getMember({ id });
    }, [id]);

    useEffect(() => {
        if (isAddMemberContributionSuccess || isUpdateMemberContributionSuccess || isArchiveMemberContributionSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isAddMemberContributionSuccess, isUpdateMemberContributionSuccess, isArchiveMemberContributionSuccess]);

    useEffect(() => {
        if (isUpdateMemberContributionSuccess) {
            getMember({ id: id as string });
        }
    }, [isUpdateMemberContributionSuccess]);

 

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError) return;
        if (getMemberError) return;

        if (isGetMemberSuccess && getMemberResponse) {
            setMember(getMemberResponse.data);
        }
    }, [isGetMemberError, isGetMemberSuccess, getMemberError, getMemberResponse, isGetMemberLoading]);

    const mutationHook = useMemo(() => {
        return isArchived ? useGetMemberArchivedContributionsMutation : useGetMemberContributionsMutation;
    }, [isArchived]);

    useEffect(() => {
        setTotalContributions(0);
    }, [isArchived]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalContributions(metadata.total);
        }
    };

    const handleArchiveContributions = async (lines: Contribution[], archive: boolean) => {
        MySwal.fire({
            title:
                lines.length === 1
                    ? archive
                        ? 'Êtes-vous sûr de vouloir archiver cette cotisation ?'
                        : 'Êtes-vous sûr de vouloir désarchiver cette cotisation ?'
                    : archive
                        ? 'Êtes-vous sûr de vouloir archiver ces cotisations ?'
                        : 'Êtes-vous sûr de vouloir désarchiver ces cotisations ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler'
        }).then(async (result) => {
            if (result.isConfirmed) {
                for (const line of lines) {
                    await archiveMemberContribution({ id: String(id), contributionId: line.id, archived: archive });
                }
            }
        });
    };

    return {
        isLoading: isGetMemberUninitialized || isGetMemberLoading,
        isError: isGetMemberError && !member,
        member,
        contributionColumnHelper,
        isArchived,
        mutationHook,
        handleArchiveContributions,
        id,
        needReload,
        handleMetadata,
        setIsArchived,
        MySwal,
        addMemberContribution,
        updateMemberContribution,
        totalContributions
    };
};
